import type { User as FirebaseUser } from 'firebase/auth';

export type AuthUser = {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  authProvider: 'email' | 'google' | 'mock';
};

export interface AuthService {
  initialize(): Promise<void>;
  signInWithEmail(email: string, password: string): Promise<AuthUser>;
  signInWithGoogle(): Promise<AuthUser>;
  signInAsMock(role: 'ADMIN' | 'VIEWER'): Promise<AuthUser>;
  signOut(): Promise<void>;
  getCurrentUser(): AuthUser | null;
  subscribeAuthState(handler: (user: AuthUser | null) => void): () => void;
  sendPasswordResetEmail(email: string): Promise<void>;
  getIdToken(): Promise<string | null>;
  refreshCurrentUser(): Promise<AuthUser | null>;
}

export const mapFirebaseUser = (user: FirebaseUser | null): AuthUser | null => {
  if (!user) return null;
  const providerId = user.providerData[0]?.providerId ?? 'password';
  const authProvider = providerId === 'google.com' ? 'google' : 'email';
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    authProvider,
  };
};

export const authServiceErrorMessage = (code: string): string => {
  const map: Record<string, string> = {
    'auth/invalid-email': '이메일 형식이 올바르지 않습니다.',
    'auth/user-disabled': '해당 계정은 사용 중지되었습니다.',
    'auth/user-not-found': '이메일 또는 비밀번호가 올바르지 않습니다.',
    'auth/wrong-password': '이메일 또는 비밀번호가 올바르지 않습니다.',
    'auth/invalid-credential': '이메일 또는 비밀번호가 올바르지 않습니다.',
    'auth/too-many-requests': '너무 많은 로그인 시도가 있었습니다. 잠시 후 다시 시도하세요.',
    'auth/network-request-failed': '네트워크 연결을 확인하세요.',
    'auth/popup-closed-by-user': 'Google 로그인 창이 닫혔습니다.',
    'auth/popup-blocked': '팝업이 차단되었습니다. 팝업 차단을 해제하세요.',
    'auth/cancelled-popup-request': '이전 로그인 시도가 진행 중입니다.',
    'auth/account-exists-with-different-credential': '이미 다른 인증 방식으로 가입된 이메일입니다.',
    'auth/unauthorized-domain': '현재 도메인은 인증이 허용되지 않았습니다.',
  };
  return map[code] || '인증 중 오류가 발생했습니다. 다시 시도하세요.';
};
